import type { MockedResponse } from "@apollo/client/testing";
import {
  PERSON_GRP_BENEFITS_QUERY,
  PERSON_GRP_FAA_CAMPAIGN_IDS_QUERY
} from "@/pages/people/[pgaId]/benefits/grp/queries";
import type { CommissionAllocationAgreementNode } from "../allocations/types";
import type { CommissionLineNode } from "../lines/types";
import { buildConnection } from "./connection";
import { commissionCampaignFixtures, commissionEnrollmentFixtures } from "./fixtures";
import { createCatalogMock, withTypenames, type MockResolver } from "./mockUtils";
import { runtimeAllocationAgreements, runtimeCommissionLines, runtimeProgramConsents } from "./runtimeState";

const GRP_PROGRAM_SUMMARY = {
  id: "commission-program-grp",
  name: "Golf Retirement Plus",
  slug: "grp"
};

function resolvePerson(pgaId: string) {
  const enrollment = commissionEnrollmentFixtures.find((node) => node.person?.pgaId === pgaId);

  return (
    enrollment?.person ?? {
      id: `person-${pgaId}`,
      displayName: `Member ${pgaId}`,
      pgaId
    }
  );
}

function buildEnrollmentMockData(pgaId: string) {
  const enrollment = commissionEnrollmentFixtures.find((node) => node.person?.pgaId === pgaId);
  if (!enrollment) {
    return null;
  }

  return {
    __typename: "BackofficeCommissionEnrollment",
    id: enrollment.id,
    accountStatus: enrollment.accountStatus,
    program: withTypenames(enrollment.program, "BackofficeCommissionProgramSummary")
  };
}

function buildProgramConsentsMockData(crmPersonId: string) {
  const nodes = runtimeProgramConsents
    .filter((consent) => consent.crmPersonId === crmPersonId)
    .map((node) => withTypenames(node, "BackofficeCommissionProgramConsent"));

  return buildConnection("BackofficeCommissionProgramConsentConnection", nodes);
}

function toMockCampaign(campaignId: string) {
  const campaign = commissionCampaignFixtures.find((node) => node.id === campaignId);
  return {
    __typename: "BackofficeCommissionCampaign",
    id: campaignId,
    editionYear: campaign?.editionYear ?? 2026,
    status: campaign?.status ?? "ACTIVE",
    partner: withTypenames(
      { id: campaign?.partner.id ?? "commission-partner-unknown", name: campaign?.partner.name ?? "Unknown Sponsor" },
      "BackofficeCommissionPartner"
    )
  };
}

function toMockAgreement(agreement: CommissionAllocationAgreementNode) {
  return {
    __typename: "BackofficeCommissionAllocationAgreement",
    ...agreement,
    campaign: toMockCampaign(agreement.commissionCampaignId),
    splits: agreement.splits.map((split) => ({
      __typename: "BackofficeCommissionAllocationSplit",
      ...split
    }))
  };
}

function buildAgreementConnection(predicate: (node: CommissionAllocationAgreementNode) => boolean) {
  // All statuses, newest first, matching pga-backend's nested agreement connections.
  const nodes = runtimeAllocationAgreements
    .filter(predicate)
    .sort((a, b) => b.effectiveStart.localeCompare(a.effectiveStart))
    .map(toMockAgreement);

  return {
    __typename: "BackofficeCommissionAllocationAgreementConnection",
    nodes,
    totalCount: nodes.length
  };
}

function withLineTypenames(line: CommissionLineNode) {
  return {
    __typename: "BackofficeCommissionLine",
    ...line,
    person: withTypenames(line.person, "BackofficeCommissionEnrollmentPerson"),
    campaign: {
      __typename: "BackofficeCommissionCampaign",
      ...line.campaign,
      partner: withTypenames(line.campaign.partner, "BackofficeCommissionPartner"),
      program: withTypenames(line.campaign.program, "BackofficeCommissionProgramSummary")
    }
  };
}

function buildLinesMockData(crmPersonId: string) {
  const nodes = runtimeCommissionLines
    .filter((line) => line.person.id === crmPersonId)
    .slice(0, 10)
    .map(withLineTypenames);

  return buildConnection("BackofficeCommissionLineConnection", nodes);
}

function buildPaidOutTotalMockData(crmPersonId: string, from: string, to: string) {
  const inRange = runtimeCommissionLines.filter((line) => {
    if (line.person.id !== crmPersonId || !line.paidOutAt) return false;
    const paidDate = line.paidOutAt.slice(0, 10);
    return paidDate >= from && paidDate <= to;
  });

  return {
    __typename: "BackofficeCommissionPaidOutTotal",
    amountCents: inRange.reduce((sum, line) => sum + line.amountCents, 0),
    lineCount: inRange.length,
    from,
    to
  };
}

export function buildPersonGrpBenefitsMockData(variables: Record<string, unknown>) {
  const pgaId = String(variables.pgaId ?? "");
  const paidOutFrom = String(variables.paidOutFrom ?? "2026-01-01");
  const paidOutTo = String(variables.paidOutTo ?? "2026-12-31");
  const person = resolvePerson(pgaId);

  return {
    person: {
      __typename: "CRMPerson",
      id: person.id,
      displayName: person.displayName,
      pgaId,
      commissionBenefitProgram: {
        __typename: "BackofficeCommissionProgramParticipation",
        program: withTypenames(GRP_PROGRAM_SUMMARY, "BackofficeCommissionProgramSummary"),
        enrollment: buildEnrollmentMockData(pgaId),
        programConsents: buildProgramConsentsMockData(person.id),
        allocationAgreements: buildAgreementConnection((node) => node.receiverCrmPersonId === person.id),
        receivedAllocationAgreements: buildAgreementConnection(
          (node) =>
            node.receiverCrmPersonId !== person.id && node.splits.some((split) => split.crmPersonId === person.id)
        ),
        lines: buildLinesMockData(person.id),
        paidOutTotal: buildPaidOutTotalMockData(person.id, paidOutFrom, paidOutTo)
      }
    }
  };
}

function buildPersonGrpFaaCampaignIdsData(personId: string, campaignIds: string[]) {
  return {
    person: {
      __typename: "CRMPerson",
      id: personId,
      commissionBenefitProgram: {
        __typename: "BackofficeCommissionProgramParticipation",
        programConsents: {
          __typename: "BackofficeCommissionProgramConsentConnection",
          nodes: campaignIds.map((commissionCampaignId) => ({
            __typename: "BackofficeCommissionProgramConsent",
            id: `consent-${personId}-${commissionCampaignId}`,
            commissionCampaignId
          }))
        }
      }
    }
  };
}

export function buildPersonGrpFaaCampaignIdsMockData(variables: Record<string, unknown>) {
  const person = resolvePerson(String(variables.pgaId ?? ""));
  const campaignIds = runtimeProgramConsents
    .filter((consent) => consent.crmPersonId === person.id)
    .map((consent) => consent.commissionCampaignId);
  return buildPersonGrpFaaCampaignIdsData(person.id, campaignIds);
}

/** Apollo mock for the Add Authorization(s) picker's existing-FAA lookup. */
export function personGrpFaaCampaignIdsApolloMock(campaignIds: string[] = [], pgaId = "12345678"): MockedResponse {
  return {
    request: { query: PERSON_GRP_FAA_CAMPAIGN_IDS_QUERY, variables: { pgaId } },
    maxUsageCount: Infinity,
    result: { data: buildPersonGrpFaaCampaignIdsData(resolvePerson(pgaId).id, campaignIds) }
  };
}

export const personGrpBenefitsMockData = buildPersonGrpBenefitsMockData({
  pgaId: "12345678",
  paidOutFrom: "2026-01-01",
  paidOutTo: "2026-12-31"
});

export const personGrpBenefitsApolloMock = createCatalogMock(PERSON_GRP_BENEFITS_QUERY, personGrpBenefitsMockData);

export const personGrpBenefitsApolloMocks: MockedResponse[] = [personGrpBenefitsApolloMock];

export const personGrpBenefitsMockResults: Record<string, Record<string, unknown>> = {
  PersonGrpBenefits: personGrpBenefitsMockData
};

export const personGrpBenefitsVariableMockResolvers: Record<string, MockResolver> = {
  PersonGrpBenefits: (variables) => buildPersonGrpBenefitsMockData(variables),
  PersonGrpFaaCampaignIds: (variables) => buildPersonGrpFaaCampaignIdsMockData(variables)
};
